import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCookieConsent } from "@/components/CookieConsentContext";

const CHOICE_BUTTON = "min-h-11 w-full rounded-lg border-2 border-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--cream)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]";

export default function CookieConsentBanner() {
  const { consent, hasChoice, saveConsent, settingsOpen, setSettingsOpen } = useCookieConsent();
  const [draft, setDraft] = useState(consent);

  useEffect(() => {
    if (settingsOpen) setDraft(consent);
  }, [consent, settingsOpen]);

  const rejectOptional = () => saveConsent({ analytics: false, marketing: false });
  const acceptAll = () => saveConsent({ analytics: true, marketing: true, affiliate: true });

  return (
    <>
      {!hasChoice && (
        <aside aria-label="Preferencias de cookies" className="no-print fixed inset-x-0 bottom-0 z-[60] border-t-2 border-[var(--ink)] bg-[var(--cream)] px-4 py-4 text-[var(--ink)] shadow-[0_-8px_30px_rgba(0,0,0,0.12)] sm:px-6">
          <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-[1fr_auto] sm:items-center">
            <div>
              <p className="font-semibold">Tu privacidad importa</p>
              <p className="mt-1 max-w-3xl text-sm text-[#5a4f46]">Usamos almacenamiento necesario para el pack. Analítica, publicidad y medición de afiliación son opcionales.</p>
              <Link to="/politica-de-cookies" className="mt-1 inline-block text-sm underline">Política de cookies</Link>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:min-w-[24rem] sm:grid-cols-3">
              <button type="button" className={CHOICE_BUTTON} onClick={acceptAll}>Aceptar</button>
              <button type="button" className={CHOICE_BUTTON} onClick={rejectOptional}>Rechazar</button>
              <button type="button" className={`${CHOICE_BUTTON} col-span-2 sm:col-span-1`} onClick={() => setSettingsOpen(true)}>Configurar</button>
            </div>
          </div>
        </aside>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-2 border-[var(--ink)] bg-[var(--cream)] text-[var(--ink)] sm:rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Configurar cookies</DialogTitle>
            <DialogDescription className="text-[#5a4f46]">Elige qué categorías opcionales permites. Puedes cambiar esta decisión desde el pie de página.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="flex items-start justify-between gap-5 border-b border-[#d9cbb8] pb-4">
              <div>
                <p className="font-semibold">Necesarias</p>
                <p className="mt-1 text-sm text-[#5a4f46]">Permiten recordar esta decisión y mantener funciones del pack, como el código de acceso y las notas.</p>
              </div>
              <span className="shrink-0 text-sm font-semibold">Siempre activas</span>
            </div>
            <label htmlFor="consent-analytics" className="flex cursor-pointer items-start justify-between gap-5 border-b border-[#d9cbb8] pb-4">
              <span>
                <span className="block font-semibold">Analítica · PostHog</span>
                <span className="mt-1 block text-sm text-[#5a4f46]">Mide visitas a páginas para entender el uso del sitio. No incluye grabación de sesión ni autocaptura.</span>
              </span>
              <Switch id="consent-analytics" checked={draft.analytics} onCheckedChange={(analytics) => setDraft((current) => ({ ...current, analytics }))} aria-label="Permitir analítica de PostHog" />
            </label>
            <label htmlFor="consent-marketing" className="flex cursor-pointer items-start justify-between gap-5">
              <span>
                <span className="block font-semibold">Publicidad · Meta Pixel</span>
                <span className="mt-1 block text-sm text-[#5a4f46]">Permite a Meta recibir eventos de visita para medición publicitaria. No se envían eventos de compra.</span>
              </span>
              <Switch id="consent-marketing" checked={draft.marketing} onCheckedChange={(marketing) => setDraft((current) => ({ ...current, marketing }))} aria-label="Permitir Meta Pixel" />
            </label>
            <label htmlFor="consent-affiliate" className="flex cursor-pointer items-start justify-between gap-5 border-t border-[#d9cbb8] pt-4">
              <span>
                <span className="block font-semibold">Medición de afiliación</span>
                <span className="mt-1 block text-sm text-[#5a4f46]">Guarda durante un máximo de 30 días el enlace profesional de referencia para atribuir una compra. Si no aceptas, no se guarda este identificador.</span>
              </span>
              <Switch id="consent-affiliate" checked={draft.affiliate} onCheckedChange={(affiliate) => setDraft((current) => ({ ...current, affiliate }))} aria-label="Permitir medición de afiliación" />
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <button type="button" className={CHOICE_BUTTON} onClick={rejectOptional}>Rechazar opcionales</button>
            <button type="button" className={CHOICE_BUTTON} onClick={() => saveConsent(draft)}>Guardar configuración</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}